<template>
  <CModal
    :show.sync="visible"
    size="xl"
    centered
    :close-on-backdrop="true"
  >
    <template #header>
      <div>
        <div class="d-flex align-items-center">
          <CIcon name="cipShield01" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">
              <translate translate-context="collaborators.modal">{{ modalTitle }}</translate>
            </h5>
            <p  class="text-help-dark m-0">
            <translate translate-context="permissions.modal">Configure what this role can access and do</translate>
          </p>
          </div>
        </div>
      </div>
      <CButtonClose @click="visible=false"/>
    </template>

    <div v-if="permissions && Object.keys(permissions).length > 0">
      <!-- Subtitle -->
      <div class="mb-3">
        <strong>
          <translate translate-context="permissions.modal">Permissions</translate>
        </strong>
        <p class="text-muted small mb-2">
          <translate translate-context="permissions.modal">Configure what this role can access and do</translate>
        </p>
      </div>

      <!-- Search -->
      <div class="search-box mb-3">
        <CInput
          v-model="searchTerm"
          :placeholder="$pgettext('permissions.modal', 'Search permissions...')"
          size="sm"
        >
          <template #prepend-content>
            <CIcon name="cilSearch" size="sm"/>
          </template>
        </CInput>
      </div>

      <!-- Scope sections -->
      <div
        v-for="section in groupedPermissions"
        :key="section.key"
        class="scope-section mb-3"
      >
        <div
          class="scope-header d-flex justify-content-between align-items-center px-3 py-2"
          :class="'scope-header-' + section.color"
          @click="toggleSection(section.key)"
        >
          <div class="d-flex align-items-center">
            <CBadge :color="section.color" class="mr-2">
              <CIcon v-if="section.icon" :name="section.icon" size="sm" class="mr-1"/>
              {{ section.label }}
            </CBadge>
            <span class="text-muted small">{{ section.activeCount }}/{{ section.totalCount }}</span>
          </div>
          <span class="d-flex align-items-center text-muted small" style="cursor: pointer;">
            {{ isSectionCollapsed(section.key)
              ? $pgettext('permissions.modal', 'Expand')
              : $pgettext('permissions.modal', 'Collapse')
            }}
            <CIcon
              :name="isSectionCollapsed(section.key) ? 'cilChevronBottom' : 'cilChevronTop'"
              size="sm"
              class="ml-1"
            />
          </span>
        </div>

        <div v-show="!isSectionCollapsed(section.key)" class="scope-body px-2 py-2">
          <Permission
            :permissions="section.permissions"
            :active="section.permissions"
            :search-filter="searchTerm"
          />
        </div>
      </div>
    </div>

    <div v-else class="text-center text-muted py-4">
      <translate translate-context="permissions.modal">No permissions found for this role</translate>
    </div>

    <template #footer>
      <CButton color="secondary" @click="visible = false">
        <translate translate-context="permissions.modal">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import Permission from '@/domain/roles/components/Permission.vue'
import CollaboratorScope from '@/domain/core/constant/collaboratorScope'

export default {
  name: 'RolePermissionsModal',

  components: {
    Permission
  },

  props: {
    scope: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      visible: false,
      permissions: null,
      role: null,
      searchTerm: '',
      collapsedSections: {}
    }
  },

  computed: {
    modalTitle() {
      if (this.role) {
        return this.$pgettext('permissions.modal', 'Permission management: %{name}').replace('%{name}', this.role.display_name || this.role.name)
      }
      return this.$pgettext('permissions.modal', 'Role Permissions')
    },

    groupedPermissions() {
      if (!this.permissions) return []

      const scopeMapping = {
        base: { key: 'base', label: 'Base', color: 'success', icon: 'cilSettings', scope: CollaboratorScope.GLOBAL },
        pica_service: { key: 'pica_service', label: 'Pica Service', color: 'success', icon: 'cilHome', scope: CollaboratorScope.PICA_SERVICE },
        gallery: { key: 'gallery', label: 'Gallery', color: 'success', icon: 'cilImage', scope: CollaboratorScope.PICA_SERVICE }
      }

      const sections = []

      for (const [key, perms] of Object.entries(this.permissions)) {
        const mapping = scopeMapping[key] || {
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          color: 'info',
          icon: 'cilBadge',
          scope: null
        }

        const totalCount = this.countPermissions(perms)
        const activeCount = this.countActivePermissions(perms)

        sections.push({
          key: mapping.key || key,
          label: mapping.label,
          color: mapping.color,
          icon: mapping.icon,
          permissions: { [key]: perms },
          totalCount,
          activeCount
        })
      }

      return sections
    }
  },

  methods: {
    show(permissions, role) {
      this.permissions = permissions
      this.role = role
      this.searchTerm = ''
      this.collapsedSections = {}
      this.visible = true
    },

    hide() {
      this.visible = false
      this.permissions = null
      this.role = null
      this.searchTerm = ''
    },

    toggleSection(key) {
      this.$set(this.collapsedSections, key, !this.collapsedSections[key])
    },

    isSectionCollapsed(key) {
      return !!this.collapsedSections[key]
    },

    countPermissions(obj) {
      if (!obj || typeof obj !== 'object') return 0
      let count = 0
      for (const value of Object.values(obj)) {
        if (typeof value === 'boolean') {
          count++
        } else if (typeof value === 'object') {
          count += this.countPermissions(value)
        }
      }
      return count
    },

    countActivePermissions(obj) {
      if (!obj || typeof obj !== 'object') return 0
      let count = 0
      for (const value of Object.values(obj)) {
        if (value === true) {
          count++
        } else if (typeof value === 'object') {
          count += this.countActivePermissions(value)
        }
      }
      return count
    }
  }
}
</script>

<style scoped>
.search-box {
  max-width: 400px;
}

.scope-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.scope-header {
  background-color: #f9fafb;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.scope-header:hover {
  background-color: #f3f4f6;
}

.scope-header-success {
  border-left: 3px solid #10b981;
}

.scope-header-info {
  border-left: 3px solid #3b82f6;
}

.scope-header-warning {
  border-left: 3px solid #f59e0b;
}

.scope-header-primary {
  border-left: 3px solid #6366f1;
}

.scope-body {
  border-top: 1px solid #e5e7eb;
}
</style>
